import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';

/**
 * Custom hook for managing Picture-in-Picture (PiP) functionality
 * Handles browser compatibility, state management, and error handling
 */
const usePictureInPicture = (localVideoStream, isVideoOnGoing,  displayNameUi) => {
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const [isPiPLoading, setIsPiPLoading] = useState(false);
  const videoElementRef = useRef(null);
  const pipWindowRef = useRef(null);
  const pipModeRef = useRef(null); // 'document' | 'element' | null
  const callbacksRef = useRef({ onMuteToggle: null, onVideoToggle: null, onExitPiP: null });
  const localVideoPipContainerRef = useRef(null);
  const localVideoRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const leavePiPHandlerRef = useRef(null);

  // Function to generate initials from display name
  const getInitials = () => {
    // Handle undefined, null, or empty string
    if (!displayNameUi || displayNameUi === 'undefined' || displayNameUi === 'undefined undefined') {
      console.log('getInitials: displayNameUi is empty or undefined, returning default');
      return 'U';
    }
    
    const name = String(displayNameUi).trim();
    if (!name || name === 'undefined' || name === 'undefined undefined') {
      console.log('getInitials: name is empty after trimming, returning default');
      return 'U';
    }
    
    console.log('getInitials called with name:', name);
    
    const words = name.split(' ').filter(word => word.length > 0);
    if (words.length === 0) {
      return 'U';
    } else if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Check browser support for Document Picture-in-Picture or element Picture-in-Picture
  useEffect(() => {
    const checkPiPSupport = () => {
      const docSupported = !!(
        'documentPictureInPicture' in window &&
        window.documentPictureInPicture &&
        'requestWindow' in window.documentPictureInPicture
      );
      const elementSupported = typeof document !== 'undefined' && !!document.createElement('video').requestPictureInPicture;
      const supported = docSupported || elementSupported;
      setIsPiPSupported(supported);

      if (!supported) {
        console.warn('Picture-in-Picture is not supported in this browser');
      }
    };

    checkPiPSupport();
  }, []);



  // Function to set callbacks
  const setPiPCallbacks = useCallback((onMuteToggle, onVideoToggle, onExitPiP) => {
    callbacksRef.current = { onMuteToggle, onVideoToggle, onExitPiP };
  }, []);

  // Function to update button states in PiP window
  const updatePiPButtonStates = useCallback((isMuted, isVideoOff) => {
    if (!pipWindowRef.current || !pipWindowRef.current.document) return;

    const muteButton = pipWindowRef.current.document.getElementById('pip-mute-btn');
    const videoButton = pipWindowRef.current.document.getElementById('pip-video-btn');

    if (muteButton) {
      // Update mute button icon and style
      if (isMuted) {
        muteButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="1" y1="1" x2="23" y2="23"></line>
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
            <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        `;
        muteButton.classList.add('muted');
      } else {
        muteButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        `;
        muteButton.classList.remove('muted');
      }
    }

    if (videoButton) {
      // Update video button icon and style
      if (isVideoOff) {
        videoButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="1" y1="1" x2="23" y2="23"></line>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            <line x1="16" y1="8" x2="20" y2="4"></line>
            <line x1="16" y1="16" x2="20" y2="20"></line>
          </svg>
        `;
        videoButton.classList.add('video-off');
      } else {
        videoButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="23 7 16 12 23 17 23 7"></polygon>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
        `;
        videoButton.classList.remove('video-off');
      }
    }
  }, []);

  // Function to update video stream in PiP window
  const updatePiPVideoStream = useCallback(() => {
    // Only applicable for Document PiP
    if (pipModeRef.current !== 'document') return;
    if (!pipWindowRef.current || !pipWindowRef.current.document) return;

    const pipVideoContainer = pipWindowRef.current.document.querySelector('.pip-video-container');
    const cameraOffMessage = pipWindowRef.current.document.querySelector('.camera-off-message');
    const pipContainer = pipWindowRef.current.document.querySelector('.pip-container');
    
    if (!pipVideoContainer || !cameraOffMessage) return;

    console.log('Updating PiP video stream...', { isVideoOnGoing, hasStream: !!localVideoStream });
    
    if (isVideoOnGoing && localVideoStream) {
      // Find the main video element with retry logic
      const findMainVideo = () => {
        // Try videoElementRef first if it was passed
        if (videoElementRef.current) {
          return videoElementRef.current;
        }
        
        // Look for video in various possible locations with specificity
        // The video is created by VideoStreamRenderer inside localVideoContainerRef
        
        // First, try to find video within the known local video container
        const localContainer = document.querySelector('[class*="local"]') || 
                              document.querySelector('[id*="local"]');
        
        if (localContainer) {
          const localVideo = localContainer.querySelector('video');
          if (localVideo && localVideo.srcObject) {
            console.log('Found video element in local container');
            return localVideo;
          }
        }
        
        // Fallback to broader selectors
        const selectors = [
          'video[data-testid="local-video"]',
          '.local-video-container video',
          'div[style*="position: relative"] video:first-of-type', // VideoStreamRenderer, prefer first
          'video[autoplay][muted]:not([id*="remote"]):not([class*="remote"])' // Exclude remote videos
        ];
        
        for (const selector of selectors) {
          const element = document.querySelector(selector);
          if (element && element.srcObject) {
            console.log('Found video element using selector:', selector);
            return element;
          }
        }
        
        return null;
      };

      let mainVideoElement = findMainVideo();
      
      // If not found immediately, wait and retry with multiple attempts
      if (!mainVideoElement || !mainVideoElement.srcObject) {
        console.log('Video element not ready, retrying...', { hasElement: !!mainVideoElement, hasSrc: !!mainVideoElement?.srcObject });
        
        // Clear any existing retry interval
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
        }
        
        let retryCount = 0;
        const maxRetries = 5;
        
        retryIntervalRef.current = setInterval(() => {
          mainVideoElement = findMainVideo();
          retryCount++;
          
          if (mainVideoElement && mainVideoElement.srcObject) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
            console.log('Video element found on retry', retryCount);
            updatePiPVideoStream(); // Retry the update
          } else if (retryCount >= maxRetries) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
            console.warn('Video element not found after max retries');
          }
        }, 150); // Check every 150ms
        
        return;
      }

      if (mainVideoElement && mainVideoElement.srcObject) {
        try {
          // Check if video element already exists in PiP
          let pipVideo = pipVideoContainer.querySelector('video');
          
          if (!pipVideo) {
            // Create a new video element for PiP
            pipVideo = pipWindowRef.current.document.createElement('video');
            pipVideo.style.width = '100%';
            pipVideo.style.height = '100%';
            pipVideo.style.objectFit = 'cover';
            pipVideo.style.transform = 'scaleX(-1)';
            pipVideo.autoplay = true;
            pipVideo.muted = true;
            pipVideo.playsInline = true;
            pipVideoContainer.appendChild(pipVideo);
            console.log('Created new video element in PiP');
          } else {
            // Ensure transform is applied to existing video element
            pipVideo.style.transform = 'scaleX(-1)';
          }
          
          // Update or set the stream with error handling
          try {
            if (pipVideo.srcObject !== mainVideoElement.srcObject) {
              pipVideo.srcObject = mainVideoElement.srcObject;
              console.log('Updated video stream in PiP');
            }
          } catch (streamError) {
            console.error('Failed to attach stream to PiP video:', streamError);
            toast.error('Unable to display video in PiP window');
            throw streamError; // Re-throw to be caught by outer try-catch
          }
          
          // Show video container and hide camera off message
          cameraOffMessage.style.display = 'none';
          pipVideoContainer.style.display = 'block';
          if (pipContainer) {
            pipContainer.classList.remove('camera-off');
          }
          console.log('PiP video container shown with video');
        } catch (e) {
          console.error('Failed to update PiP video stream', e);
        }
      }
    } else {
      // Video is off, clear container and show message
      pipVideoContainer.innerHTML = '';
      pipVideoContainer.style.display = 'none';
      cameraOffMessage.style.display = 'flex';
      if (pipContainer) {
        pipContainer.classList.add('camera-off');
      }
      console.log('PiP video container cleared (video off)');
    }
  }, [isVideoOnGoing, localVideoStream]);

  // Video stream sharing logic for PiP window (Document PiP)
  // This effect ensures video updates when stream/state changes
  useEffect(() => {
    if (pipModeRef.current !== 'document') return;
    if (!isPiPActive) return;

    // Use the consolidated update function
    updatePiPVideoStream();
  }, [localVideoStream, isVideoOnGoing, isPiPActive, updatePiPVideoStream]);

  // Update avatar initials when displayNameUi changes
  useEffect(() => {
    if (pipModeRef.current !== 'document') return;
    if (!isPiPActive) return;
    if (!pipWindowRef.current) return;

    const avatarElement = pipWindowRef.current.document.getElementById('camera-off-avatar');
    if (avatarElement) {
      const initials = getInitials();
      avatarElement.textContent = initials;
      console.log('Updated PiP avatar initials to:', initials);
    }
  }, [displayNameUi, isPiPActive]);

  // Enter Picture-in-Picture mode using Document PiP if available, otherwise element PiP
  const enterPiP = useCallback(async (videoElement) => {
    if (!isPiPSupported) {
      toast.error('Picture-in-Picture is not supported in this browser');
      return false;
    }

    if (isPiPActive) {
      toast.warning('Picture-in-Picture is already active');
      return false;
    }

    if (isPiPLoading) {
      toast.warning('Picture-in-Picture request is already in progress');
      return false;
    }

    const docSupported = !!(
      'documentPictureInPicture' in window &&
      window.documentPictureInPicture &&
      'requestWindow' in window.documentPictureInPicture
    );

    try {
      setIsPiPLoading(true);

      if (docSupported) {
        // Check if a Document PiP window is already open
        if (window.documentPictureInPicture.window) {
          console.warn('A Document Picture-in-Picture window is already open');
          toast.warning('Picture-in-Picture is already open in another location');
          setIsPiPLoading(false);
          return false;
        }
        // Document PiP path: videoElement is optional
        if (videoElement && !(videoElement instanceof HTMLVideoElement)) {
          toast.error('Invalid video element provided');
          setIsPiPLoading(false);
          return false;
        }

        videoElementRef.current = videoElement || null;
        const pipWindow = await window.documentPictureInPicture.requestWindow({
          width: 200,
          height: 170
        });
        pipWindowRef.current = pipWindow;
        pipModeRef.current = 'document';

      // Create the HTML content for the PiP window
      pipWindow.document.body.innerHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Video Call - Picture in Picture</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              background: #000;
              overflow: hidden;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }
            
            .pip-container {
              position: relative;
              width: 100%;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .pip-video {
              width: 100%;
              height: 100%;
              object-fit: cover;
              background: #000;
            }
            
            .pip-video-container {
              width: 100%;
              height: 100%;
              background: #000;
              position: relative;
            }
            
            .pip-controls {
              position: absolute;
              top: 10px;
              right: 10px;
              display: flex;
              gap: 8px;
              opacity: 1;
              z-index: 99;
              transition: opacity 0.3s ease;
            }
            
            .pip-container:hover .pip-controls {
              opacity: 1;
            }
            
            .pip-container.camera-off .pip-controls {
              opacity: 1;
            }
            
            .pip-btn {
              width: 36px;
              height: 36px;
              border: none;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.1);
              color: white;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
              backdrop-filter: blur(10px);
            }
            
            .pip-btn:hover {
              background: rgba(255, 255, 255, 0.2);
              transform: scale(1.05);
            }
            
            .pip-btn:active {
              transform: scale(0.95);
            }
            
            .pip-btn svg {
              width: 15px;
              height: 15px;
            }
            
            .pip-btn.muted {
              background: rgba(220, 53, 69, 0.3);
            }
            
            .pip-btn.video-off {
              background: rgba(220, 53, 69, 0.3);
            }
            
            .pip-btn.exit {
              background: #dc3545;
              border-radius: 6px;
              width: 40px;
              height: 32px;
            }
            
            .pip-btn.exit:hover {
              background: #c82333;
            }
            
            .camera-off-message {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: none;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: #1a1a1a;
              z-index: 10;
            }
            
            .camera-off-avatar {
              width: 50px;
              height: 50px;
              border-radius: 50%;
              background: #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              font-weight: 600;
              color: #1a1a1a;
              letter-spacing: 0px;
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
              border: 2px solid rgba(255, 255, 255, 0.1);
            }
          </style>
        </head>
        <body>
          <div class="pip-container">
            <div class="pip-video-container" id="pip-video-container"></div>
            <div class="camera-off-message">
              <div class="camera-off-avatar" id="camera-off-avatar">${getInitials()}</div>
            </div>
            <div class="pip-controls">
              <button id="pip-video-btn" class="pip-btn" title="Video On/Off">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M23 7l-7 5 7 5V7z"></path>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              </button>
              <button id="pip-mute-btn" class="pip-btn" title="Mute/Unmute">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              </button>
              <button id="pip-exit-btn" class="pip-btn exit" title="End Call">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </button>
            </div>
          </div>
        </body>
        </html>
      `;

      // Get the video container and camera off message from the PiP window
      const pipVideoContainer = pipWindow.document.querySelector('.pip-video-container');
      const cameraOffMessage = pipWindow.document.querySelector('.camera-off-message');
      
      // Set up the refs for the VideoStreamRenderer
      localVideoPipContainerRef.current = pipVideoContainer;
      localVideoRef.current = pipVideoContainer;
      
      // Set initial state based on video status
      const pipContainer = pipWindow.document.querySelector('.pip-container');
      if (isVideoOnGoing && localVideoStream) {
        pipVideoContainer.style.display = 'block';
        cameraOffMessage.style.display = 'none';
        if (pipContainer) {
          pipContainer.classList.remove('camera-off');
        }
      } else {
        pipVideoContainer.style.display = 'none';
        cameraOffMessage.style.display = 'flex';
        if (pipContainer) {
          pipContainer.classList.add('camera-off');
        }
      }

      // Set up event listeners for the PiP window
      pipWindow.addEventListener('pagehide', () => {
        console.log('PiP window closed');
        setIsPiPActive(false);
        setIsPiPLoading(false);
        pipWindowRef.current = null;
      });

      // Set up button event listeners
      const muteBtn = pipWindow.document.getElementById('pip-mute-btn');
      const videoBtn = pipWindow.document.getElementById('pip-video-btn');
      const exitBtn = pipWindow.document.getElementById('pip-exit-btn');

      if (muteBtn && callbacksRef.current.onMuteToggle) {
        muteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('PiP Mute button clicked, calling callback:', callbacksRef.current.onMuteToggle);
          callbacksRef.current.onMuteToggle();
        });
      } else {
        console.warn('Mute button or callback not available:', { muteBtn, callback: callbacksRef.current.onMuteToggle });
      }

      if (videoBtn && callbacksRef.current.onVideoToggle) {
        videoBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('PiP Video button clicked, calling callback:', callbacksRef.current.onVideoToggle);
          callbacksRef.current.onVideoToggle();
        });
      } else {
        console.warn('Video button or callback not available:', { videoBtn, callback: callbacksRef.current.onVideoToggle });
      }

      if (exitBtn && callbacksRef.current.onExitPiP) {
        exitBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('PiP Exit button clicked, calling callback:', callbacksRef.current.onExitPiP);
          callbacksRef.current.onExitPiP();
        });
      } else {
        console.warn('Exit button or callback not available:', { exitBtn, callback: callbacksRef.current.onExitPiP });
      }

        setIsPiPActive(true);
        setIsPiPLoading(false);
        console.log('Successfully entered Document Picture-in-Picture mode');
        return true;
      }

      // Element PiP fallback requires a valid video element
      if (!(videoElement instanceof HTMLVideoElement)) {
        toast.error('No video element available for Picture-in-Picture');
        setIsPiPLoading(false);
        return false;
      }

      await videoElement.requestPictureInPicture();
      videoElementRef.current = videoElement;
      pipWindowRef.current = null;
      pipModeRef.current = 'element';

      // Attach leave event to update state with proper cleanup
      const handleLeave = () => {
        console.log('Element PiP left via leavepictureinpicture event');
        setIsPiPActive(false);
        if (leavePiPHandlerRef.current) {
          document.removeEventListener('leavepictureinpicture', leavePiPHandlerRef.current);
          leavePiPHandlerRef.current = null;
        }
      };
      
      // Remove any existing listener before adding new one
      if (leavePiPHandlerRef.current) {
        document.removeEventListener('leavepictureinpicture', leavePiPHandlerRef.current);
      }
      
      leavePiPHandlerRef.current = handleLeave;
      document.addEventListener('leavepictureinpicture', handleLeave);

      setIsPiPActive(true);
      setIsPiPLoading(false);
      console.log('Successfully entered Element Picture-in-Picture mode');
      return true;
    } catch (error) {
      console.error('Failed to enter Picture-in-Picture:', error);
      setIsPiPLoading(false);
      if (error && error.name === 'NotAllowedError') {
        toast.error('Picture-in-Picture request was denied. Please allow it in your browser settings.');
      } else if (error && error.name === 'NotSupportedError') {
        toast.error('Picture-in-Picture is not supported on this device');
      } else if (error && error.name === 'InvalidStateError') {
        toast.error('Cannot enter Picture-in-Picture in current state');
      } else if (error && error.name === 'AbortError') {
        toast.error('Picture-in-Picture request was cancelled');
      } else {
        toast.error('Failed to enter Picture-in-Picture mode');
      }
      return false;
    }
  }, [isPiPSupported, isPiPActive, isPiPLoading]);

  // Exit Picture-in-Picture mode
  const exitPiP = useCallback(async () => {
    if (!isPiPActive) {
      toast.warning('Picture-in-Picture is not active');
      return false;
    }

    try {
      if (pipModeRef.current === 'document') {
        if (pipWindowRef.current) {
          pipWindowRef.current.close();
          console.log('Successfully exited Document Picture-in-Picture mode');
          return true;
        }
      } else if (pipModeRef.current === 'element') {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
          console.log('Successfully exited Element Picture-in-Picture mode');
          return true;
        }
      }
      // Fallback: just update state if no active PiP window
      setIsPiPActive(false);
      pipWindowRef.current = null;
      pipModeRef.current = null;
      return true;
    } catch (error) {
      console.error('Failed to exit Picture-in-Picture:', error);
      toast.error('Failed to exit Picture-in-Picture mode');
      return false;
    }
  }, [isPiPActive]);

  // Toggle Picture-in-Picture mode
  const togglePiP = useCallback(async (videoElement) => {
    if (isPiPActive) {
      return await exitPiP();
    } else {
      return await enterPiP(videoElement);
    }
  }, [isPiPActive, enterPiP, exitPiP]);

  // Get the current video element in PiP
  const getPiPVideoElement = useCallback(() => {
    if (pipModeRef.current === 'element') {
      return document.pictureInPictureElement;
    }
    return videoElementRef.current;
  }, []);

  // Check if a specific video element is in PiP
  const isVideoInPiP = useCallback((videoElement) => {
    if (pipModeRef.current === 'element') {
      return document.pictureInPictureElement === videoElement;
    }
    return videoElementRef.current === videoElement && isPiPActive;
  }, [isPiPActive]);

  // Utility function to check and fix PiP blocking attributes
  const checkAndFixPiPBlocking = useCallback((videoElement) => {
    const issues = [];
    
    // Check video element itself
    if (videoElement.hasAttribute('disablePictureInPicture')) {
      issues.push('Video element has disablePictureInPicture attribute');
      videoElement.removeAttribute('disablePictureInPicture');
    }
    
    // Check parent elements
    let parentElement = videoElement.parentElement;
    while (parentElement && parentElement !== document.body) {
      if (parentElement.hasAttribute('disablePictureInPicture')) {
        issues.push(`Parent element has disablePictureInPicture attribute: ${parentElement.tagName}`);
        parentElement.removeAttribute('disablePictureInPicture');
      }
      parentElement = parentElement.parentElement;
    }
    
    // Check for CSS-based PiP blocking
    const computedStyle = window.getComputedStyle(videoElement);
    if (computedStyle.pointerEvents === 'none') {
      issues.push('Video element has pointer-events: none');
      videoElement.style.pointerEvents = 'auto';
    }
    
    // Check if video is in a container with overflow hidden that might interfere
    let checkElement = videoElement.parentElement;
    while (checkElement && checkElement !== document.body) {
      const elementStyle = window.getComputedStyle(checkElement);
      if (elementStyle.overflow === 'hidden' && 
          (elementStyle.position === 'absolute' || elementStyle.position === 'fixed')) {
        console.warn('Video is in a container with overflow:hidden and absolute/fixed positioning, this might interfere with PiP');
      }
      checkElement = checkElement.parentElement;
    }
    
    if (issues.length > 0) {
      console.log('Fixed PiP blocking issues:', issues);
    }
    
    return issues.length === 0;
  }, []);

  // Force exit PiP (useful for cleanup)
  const forceExitPiP = useCallback(() => {
    // Clear any pending retry intervals
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
    
    // Clean up Element PiP event listener
    if (leavePiPHandlerRef.current) {
      document.removeEventListener('leavepictureinpicture', leavePiPHandlerRef.current);
      leavePiPHandlerRef.current = null;
    }
    
    setIsPiPActive(false);
    setIsPiPLoading(false);
    videoElementRef.current = null;
    pipWindowRef.current = null;
    pipModeRef.current = null;
  }, []);

  // Handle video state changes while in PiP (Element PiP only)
  useEffect(() => {
    // Only apply to element PiP mode, not document PiP
    if (pipModeRef.current !== 'element') return;
    if (!isPiPActive || !videoElementRef.current) return;

    const videoElement = videoElementRef.current;
    
    const handleVideoEnded = () => {
      console.log('Video ended while in Element PiP, exiting PiP');
      forceExitPiP();
    };

    const handleVideoError = () => {
      console.log('Video error while in Element PiP, exiting PiP');
      forceExitPiP();
    };

    const handleVideoPause = () => {
      console.log('Video paused while in Element PiP, exiting PiP');
      forceExitPiP();
    };

    videoElement.addEventListener('ended', handleVideoEnded);
    videoElement.addEventListener('error', handleVideoError);
    videoElement.addEventListener('pause', handleVideoPause);

    return () => {
      videoElement.removeEventListener('ended', handleVideoEnded);
      videoElement.removeEventListener('error', handleVideoError);
      videoElement.removeEventListener('pause', handleVideoPause);
    };
  }, [isPiPActive, forceExitPiP]);

  return {
    // State
    isPiPActive,
    isPiPSupported,
    isPiPLoading,
    
    // Actions
    enterPiP,
    exitPiP,
    togglePiP,
    forceExitPiP,
    
    // Utilities
    getPiPVideoElement,
    isVideoInPiP,
    checkAndFixPiPBlocking,
    setPiPCallbacks,
    updatePiPButtonStates,
    updatePiPVideoStream,
  };
};

export default usePictureInPicture;
